// frontend/src/lib/apiScheduling.ts

/**
 * API client for Scheduling feature (Calendly-like functionality).
 * Handles calendar connections, availability settings, teams, and bookings.
 */

import { apiClient } from './api';
import type {
  CalendarConnection,
  AvailabilitySettings,
  AvailabilitySettingsForm,
  Team,
  TeamForm,
  ScheduledMeeting,
  BookingRequest,
  TimeSlot,
  AvailabilityResponse,
  TeamStatsResponse,
  OAuthResponse,
  CalendarProvider,
} from '../types/scheduling';

const SCHEDULING_BASE = '/api/scheduling';

export const schedulingApi = {
  // ========== Calendar Connections ==========

  /**
   * Get list of user's calendar connections
   */
  async getCalendarConnections(): Promise<CalendarConnection[]> {
    const response = await apiClient.get<{ results: CalendarConnection[] }>(`${SCHEDULING_BASE}/calendars/`);
    return response.data.results || [];
  },

  /**
   * Initiate OAuth flow for calendar provider
   * Returns OAuth authorization URL to redirect user to
   */
  async connectCalendar(provider: CalendarProvider): Promise<OAuthResponse> {
    const response = await apiClient.get<OAuthResponse>(`${SCHEDULING_BASE}/calendars/connect/${provider}/`);
    return response.data;
  },

  /**
   * Disconnect/delete a calendar connection
   */
  async disconnectCalendar(connectionId: string): Promise<void> {
    await apiClient.delete(`${SCHEDULING_BASE}/calendars/${connectionId}/`);
  },

  /**
   * Force sync with calendar provider
   */
  async syncCalendar(connectionId: string): Promise<{ message: string; calendars: Array<{ id: string; name: string; primary?: boolean }> }> {
    const response = await apiClient.post<{ message: string; calendars: Array<{ id: string; name: string; primary?: boolean }> }>(`${SCHEDULING_BASE}/calendars/${connectionId}/sync/`, {});
    return response.data;
  },

  // ========== Availability Settings ==========

  /**
   * Get user's availability settings (creates default if doesn't exist)
   */
  async getAvailabilitySettings(): Promise<AvailabilitySettings | null> {
    const response = await apiClient.get<AvailabilitySettings | AvailabilitySettings[]>(`${SCHEDULING_BASE}/settings/`);
    // Backend list() returns a single object (not an array) due to custom implementation
    if (Array.isArray(response.data)) {
      return response.data[0] || null;
    }
    return response.data || null;
  },

  /**
   * Update user's availability settings
   */
  async updateAvailabilitySettings(settings: Partial<AvailabilitySettingsForm>): Promise<AvailabilitySettings> {
    // Get current settings to get the ID
    const current = await this.getAvailabilitySettings();
    if (!current) {
      throw new Error('No availability settings found. Please create settings first.');
    }
    const response = await apiClient.patch<AvailabilitySettings>(`${SCHEDULING_BASE}/settings/${current.id}/`, settings);
    return response.data;
  },

  /**
   * Preview available time slots for next 7 days
   */
  async previewAvailability(): Promise<{ slots: TimeSlot[]; total_count: number }> {
    const response = await apiClient.get<{ slots: TimeSlot[]; total_count: number }>(`${SCHEDULING_BASE}/settings/preview/`);
    return response.data;
  },

  // ========== Teams ==========

  /**
   * Get list of scheduling teams
   */
  async getTeams(): Promise<Team[]> {
    const response = await apiClient.get<{ results: Team[] }>(`${SCHEDULING_BASE}/teams/`);
    return response.data.results || response.data;
  },

  /**
   * Get a specific team
   */
  async getTeam(teamId: string): Promise<Team> {
    const response = await apiClient.get<Team>(`${SCHEDULING_BASE}/teams/${teamId}/`);
    return response.data;
  },

  /**
   * Create a new team
   */
  async createTeam(teamData: TeamForm): Promise<Team> {
    const response = await apiClient.post<Team>(`${SCHEDULING_BASE}/teams/`, teamData);
    return response.data;
  },

  /**
   * Update a team
   */
  async updateTeam(teamId: string, teamData: Partial<TeamForm>): Promise<Team> {
    const response = await apiClient.patch<Team>(`${SCHEDULING_BASE}/teams/${teamId}/`, teamData);
    return response.data;
  },

  /**
   * Delete a team
   */
  async deleteTeam(teamId: string): Promise<void> {
    await apiClient.delete(`${SCHEDULING_BASE}/teams/${teamId}/`);
  },

  /**
   * Add a member to a team
   */
  async addTeamMember(teamId: string, userId: number): Promise<Team> {
    const response = await apiClient.post<Team>(`${SCHEDULING_BASE}/teams/${teamId}/add-member/`, { user_id: userId });
    return response.data;
  },

  /**
   * Remove a member from a team
   */
  async removeTeamMember(teamId: string, userId: number): Promise<Team> {
    const response = await apiClient.post<Team>(`${SCHEDULING_BASE}/teams/${teamId}/remove-member/`, { user_id: userId });
    return response.data;
  },

  /**
   * Get assignment statistics for a team
   */
  async getTeamStats(teamId: string, days: number = 30): Promise<TeamStatsResponse> {
    const response = await apiClient.get<TeamStatsResponse>(`${SCHEDULING_BASE}/teams/${teamId}/stats/?days=${days}`);
    return response.data;
  },

  // ========== Scheduled Meetings ==========

  /**
   * Get list of scheduled meetings
   */
  async getMeetings(): Promise<ScheduledMeeting[]> {
    const response = await apiClient.get<{ results: ScheduledMeeting[] }>(`${SCHEDULING_BASE}/meetings/`);
    return response.data.results || response.data;
  },

  /**
   * Get upcoming meetings for the current user
   */
  async getUpcomingMeetings(): Promise<ScheduledMeeting[]> {
    const response = await apiClient.get<ScheduledMeeting[]>(`${SCHEDULING_BASE}/meetings/upcoming/`);
    return response.data;
  },

  /**
   * Cancel a meeting
   */
  async cancelMeeting(meetingId: string, reason?: string): Promise<ScheduledMeeting> {
    const response = await apiClient.post<ScheduledMeeting>(`${SCHEDULING_BASE}/meetings/${meetingId}/cancel/`, { reason });
    return response.data;
  },

  // ========== Public Booking API (No Auth) ==========

  /**
   * Get available time slots for a user (public, no auth required)
   */
  async getTeamAvailability(org: string, team: string): Promise<AvailabilityResponse> {
    return (await apiClient.get<AvailabilityResponse>(`${SCHEDULING_BASE}/book/${org}/team/${team}/`)).data;
  },
  async confirmTeamBooking(org: string, team: string, data: BookingRequest): Promise<ScheduledMeeting> {
    return (await apiClient.post<ScheduledMeeting>(`${SCHEDULING_BASE}/book/${org}/team/${team}/`, data)).data;
  },

  async getUserAvailability(orgSlug: string, username: string, days: number = 14): Promise<AvailabilityResponse> {
    const response = await apiClient.get<AvailabilityResponse>(
      `${SCHEDULING_BASE}/book/${orgSlug}/${username}/?days=${days}`
    );
    return response.data;
  },

  /**
   * Confirm a booking with a user (public, no auth required)
   */
  async confirmBooking(orgSlug: string, username: string, bookingData: BookingRequest): Promise<ScheduledMeeting> {
    const response = await apiClient.post<ScheduledMeeting>(
      `${SCHEDULING_BASE}/book/${orgSlug}/${username}/confirm/`,
      bookingData
    );
    return response.data;
  },

  /**
   * Cancel a meeting (public cancellation link)
   */
  async cancelMeetingPublic(meetingId: string): Promise<{ message: string }> {
    const response = await apiClient.get<{ message: string }>(`${SCHEDULING_BASE}/meetings/cancel/${meetingId}/`);
    return response.data;
  },
};
